<template>
  <div class="lesson-packages-details">
    <!-- Summary Cards -->
    <div class="row mb-4">
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="PackageIcon" size="24" class="text-primary mb-2" />
            <h4 class="mb-1">{{ packagesData.length || 0 }}</h4>
            <small class="text-muted">Всего пакетов</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="CheckCircleIcon" size="24" class="text-success mb-2" />
            <h4 class="mb-1">{{ getActiveCount() }}</h4>
            <small class="text-muted">Активных</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="BookIcon" size="24" class="text-info mb-2" />
            <h4 class="mb-1">{{ getTotalLessons() }}</h4>
            <small class="text-muted">Всего уроков</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="ClockIcon" size="24" class="text-warning mb-2" />
            <h4 class="mb-1">{{ getRemainingLessons() }}</h4>
            <small class="text-muted">Осталось</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Usage Chart -->
    <div class="row mb-4">
      <div class="col-lg-8 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Использование пакетов</h5>
          </div>
          <div class="card-body">
            <div v-if="packagesData.length > 0">
              <div
                v-for="package_item in packagesData"
                :key="package_item.id"
                class="package-usage mb-4"
              >
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h6 class="mb-0">{{ package_item.package_name }}</h6>
                    <small class="text-muted">{{ getPackageTypeText(package_item.package_type) }}</small>
                  </div>
                  <b-badge :variant="getStatusVariant(package_item.status)">
                    {{ getStatusText(package_item.status) }}
                  </b-badge>
                </div>
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span>Использовано: {{ package_item.lessons_used }}/{{ package_item.lessons_total }}</span>
                  <span>{{ getUsagePercentage(package_item.lessons_used, package_item.lessons_total) }}%</span>
                </div>
                <b-progress
                  :value="getUsagePercentage(package_item.lessons_used, package_item.lessons_total)"
                  :variant="getUsageVariant(getUsagePercentage(package_item.lessons_used, package_item.lessons_total))"
                  height="10px"
                />
                <div class="row mt-2">
                  <div class="col-md-6">
                    <small class="text-muted">Куплен: {{ formatDate(package_item.purchased_at) }}</small>
                  </div>
                  <div class="col-md-6">
                    <small v-if="package_item.expires_at" :class="getExpiryClass(package_item.expires_at)">
                      Истекает: {{ formatDate(package_item.expires_at) }}
                    </small>
                    <small v-else class="text-muted">Без ограничений</small>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-3">
              <p class="text-muted">Пакеты уроков не найдены</p>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-4 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Статистика</h5>
          </div>
          <div class="card-body">
            <div class="stats-list">
              <div class="stat-item d-flex justify-content-between align-items-center mb-3">
                <span>Общая стоимость:</span>
                <strong>{{ getTotalCost() }} ₽</strong>
              </div>
              <div class="stat-item d-flex justify-content-between align-items-center mb-3">
                <span>Средняя цена урока:</span>
                <strong>{{ getAverageCostPerLesson() }} ₽</strong>
              </div>
              <div class="stat-item d-flex justify-content-between align-items-center mb-3">
                <span>Истекают скоро:</span>
                <strong>{{ getExpiringSoon() }}</strong>
              </div>
              <div class="stat-item d-flex justify-content-between align-items-center">
                <span>Неактивных пакетов:</span>
                <strong>{{ getInactiveCount() }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Packages List -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Детали пакетов</h5>
            <div>
              <b-form-select
                v-model="statusFilter"
                :options="statusOptions"
                size="sm"
                style="width: auto;"
              />
            </div>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>Пакет</th>
                    <th>Дата покупки</th>
                    <th>Статус</th>
                    <th>Уроки</th>
                    <th>Использовано</th>
                    <th>Осталось</th>
                    <th>Стоимость</th>
                    <th>Истекает</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="package_item in filteredPackages"
                    :key="package_item.id"
                    class="package-row"
                  >
                    <td>
                      <div>
                        <strong>{{ package_item.package_name }}</strong>
                        <br>
                        <small class="text-muted">{{ getPackageTypeText(package_item.package_type) }}</small>
                      </div>
                    </td>
                    <td>{{ formatDate(package_item.purchased_at) }}</td>
                    <td>
                      <b-badge :variant="getStatusVariant(package_item.status)">
                        {{ getStatusText(package_item.status) }}
                      </b-badge>
                    </td>
                    <td>{{ package_item.lessons_total }}</td>
                    <td>
                      <span class="text-warning">{{ package_item.lessons_used }}</span>
                    </td>
                    <td>
                      <span class="text-success">{{ package_item.lessons_remaining }}</span>
                    </td>
                    <td>{{ package_item.price_paid || 0 }} ₽</td>
                    <td>
                      <span v-if="package_item.expires_at" :class="getExpiryClass(package_item.expires_at)">
                        {{ formatDate(package_item.expires_at) }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="filteredPackages.length === 0" class="text-center py-5">
              <feather-icon icon="PackageIcon" size="48" class="text-muted mb-3" />
              <h5 class="text-muted">Пакеты не найдены</h5>
              <p class="text-muted">Попробуйте изменить фильтр статуса</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BBadge,
  BProgress,
  BFormSelect,
  BCard,
  BCardHeader,
  BCardBody
} from 'bootstrap-vue'

export default {
  name: 'LessonPackagesDetails',
  components: {
    BBadge,
    BProgress,
    BFormSelect,
    BCard,
    BCardHeader,
    BCardBody
  },
  props: {
    packagesData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      statusFilter: 'all',
      statusOptions: [
        { value: 'all', text: 'Все статусы' },
        { value: 'active', text: 'Активные' },
        { value: 'expired', text: 'Истекшие' },
        { value: 'cancelled', text: 'Отмененные' },
        { value: 'suspended', text: 'Приостановленные' }
      ]
    }
  },
  computed: {
    filteredPackages() {
      if (this.statusFilter === 'all') {
        return this.packagesData
      }
      return this.packagesData.filter(pkg => pkg.status === this.statusFilter)
    }
  },
  methods: {
    getActiveCount() {
      return this.packagesData.filter(pkg => pkg.status === 'active').length
    },
    getInactiveCount() {
      return this.packagesData.filter(pkg => pkg.status !== 'active').length
    },
    getTotalLessons() {
      return this.packagesData.reduce((sum, pkg) => sum + (pkg.lessons_total || 0), 0)
    },
    getRemainingLessons() {
      return this.packagesData.reduce((sum, pkg) => sum + (pkg.lessons_remaining || 0), 0)
    },
    getTotalCost() {
      const total = this.packagesData.reduce((sum, pkg) => sum + (pkg.price_paid || 0), 0)
      return total.toLocaleString('ru-RU')
    },
    getAverageCostPerLesson() {
      const totalCost = this.packagesData.reduce((sum, pkg) => sum + (pkg.price_paid || 0), 0)
      const totalLessons = this.getTotalLessons()
      if (totalLessons === 0) return '0'
      return Math.round(totalCost / totalLessons).toLocaleString('ru-RU')
    },
    getExpiringSoon() {
      return this.packagesData.filter(pkg => {
        if (!pkg.expires_at) return false
        const now = new Date()
        const expiry = new Date(pkg.expires_at)
        const daysDiff = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 3600 * 24))
        return daysDiff <= 30 && daysDiff > 0
      }).length
    },
    getUsagePercentage(used, total) {
      if (total === 0) return 0
      return Math.round((used / total) * 100)
    },
    getUsageVariant(percentage) {
      if (percentage >= 90) return 'danger'
      if (percentage >= 70) return 'warning'
      return 'success'
    },
    getStatusVariant(status) {
      const variants = {
        active: 'success',
        expired: 'danger',
        cancelled: 'secondary',
        suspended: 'warning'
      }
      return variants[status] || 'secondary'
    },
    getStatusText(status) {
      const texts = {
        active: 'Активный',
        expired: 'Истекший',
        cancelled: 'Отмененный',
        suspended: 'Приостановленный'
      }
      return texts[status] || status
    },
    getPackageTypeText(type) {
      const types = {
        lessons: 'Уроки',
        unlimited: 'Безлимитный',
        trial: 'Пробный',
        subscription: 'Подписка'
      }
      return types[type] || type
    },
    getExpiryClass(expiryDate) {
      const now = new Date()
      const expiry = new Date(expiryDate)
      const daysDiff = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 3600 * 24))

      if (daysDiff < 0) return 'text-danger font-weight-bold'
      if (daysDiff <= 7) return 'text-danger'
      if (daysDiff <= 30) return 'text-warning'
      return 'text-muted'
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.lesson-packages-details {
  .stats-card {
    border: none;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .package-usage {
    padding: 1rem;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    background: #f8f9fa;
  }

  .stats-list {
    .stat-item {
      padding: 0.5rem 0;
      border-bottom: 1px solid #e9ecef;

      &:last-child {
        border-bottom: none;
      }
    }
  }

  .table .package-row:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  .thead-light th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #6c757d;
    border-top: none;
  }
}

.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.dark-layout {
  .lesson-packages-details {
    .stats-card,
    .card {
      background-color: #283046;
      color: #d0d2d6;
    }

    .package-usage {
      background-color: #404656;
      border-color: #555;
    }

    .stats-list .stat-item {
      border-bottom-color: #555;
    }

    .thead-light th {
      background-color: #404656;
      color: #b4b7bd;
      border-color: #555;
    }

    .table {
      color: #d0d2d6;

      .package-row:hover {
        background-color: rgba(255, 255, 255, 0.05);
      }

      td {
        border-color: #555;
      }
    }

    .text-muted {
      color: #b4b7bd !important;
    }
  }
}
</style>